import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Ruler, X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { useCart } from "@/lib/store";
import { toast } from "sonner";

const ProductGallery = ({ open, onClose, slides, start = 0, title }) => {
  const [idx, setIdx] = useState(start);
  const go = (n) => setIdx((prev) => (prev + n + slides.length) % slides.length);
  if (!slides || slides.length === 0) return null;
  return (
    <AnimatePresence onExitComplete={() => setIdx(start)}>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 z-[130] bg-black/90 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            data-testid="product-gallery" className="fixed z-[140] inset-0 flex flex-col items-center justify-center p-4 sm:p-10 pointer-events-none">
            <div className="w-full max-w-2xl pointer-events-auto">
              <div className="flex items-center justify-between mb-4">
                <span className="font-anton uppercase text-xl tracking-tight text-white">{title}</span>
                <button data-testid="gallery-close" onClick={onClose} className="text-white/70 hover:text-white"><X className="w-7 h-7" /></button>
              </div>
              <div className="relative bg-[var(--surface)] border border-[var(--line)] overflow-hidden aspect-square touch-pan-y">
                <AnimatePresence mode="wait" custom={idx}>
                  <motion.img key={idx} src={slides[idx].src} alt={slides[idx].label}
                    initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.6}
                    onDragEnd={(e, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); }}
                    className="w-full h-full object-cover cursor-grab active:cursor-grabbing select-none" draggable={false} />
                </AnimatePresence>
                <span className="absolute top-4 left-4 bg-black/70 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">{slides[idx].label}</span>
                {slides.length > 1 && (
                  <>
                    <button data-testid="gallery-prev" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[var(--accent)] flex items-center justify-center text-white transition-colors"><ChevronLeft className="w-6 h-6" /></button>
                    <button data-testid="gallery-next" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[var(--accent)] flex items-center justify-center text-white transition-colors"><ChevronRight className="w-6 h-6" /></button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 mt-4">
                {slides.map((s, i) => (
                  <button key={i} data-testid={`gallery-dot-${i}`} onClick={() => setIdx(i)}
                    className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[var(--accent)]" : "w-2 bg-white/30 hover:bg-white/60"}`} aria-label={s.label} />
                ))}
              </div>
              <p className="text-center text-white/50 text-xs uppercase tracking-widest mt-3">Swipe or use arrows to see front & back</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const SIZE_CHART = [
  { size: "S", chest: '36-38"', length: '27"' },
  { size: "M", chest: '39-41"', length: '28"' },
  { size: "L", chest: '42-44"', length: '29"' },
  { size: "XL", chest: '45-47"', length: '30"' },
  { size: "XXL", chest: '48-50"', length: '31"' },
];

const SizeGuide = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm" />
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }} data-testid="size-guide-modal"
          className="fixed z-[140] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg bg-[var(--surface)] border border-[var(--line)] p-8">
          <h3 className="font-anton uppercase text-3xl tracking-tight mb-2">Size Guide</h3>
          <p className="text-[var(--text-dim)] text-sm mb-6">
            Regular fit, true to size. Measurements are garment dimensions. Between sizes? Size up for a relaxed drape.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[var(--text-dim)] uppercase tracking-widest text-xs border-b border-[var(--line)]">
                <th className="text-left py-3">Size</th><th className="text-left py-3">Chest</th><th className="text-left py-3">Length</th>
              </tr>
            </thead>
            <tbody>
              {SIZE_CHART.map((r) => (
                <tr key={r.size} className="border-b border-[var(--line)]">
                  <td className="py-3 font-bold">{r.size}</td><td className="py-3">{r.chest}</td><td className="py-3">{r.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button data-testid="close-size-guide" onClick={onClose}
            className="mt-6 w-full bg-[var(--text)] text-black py-3 uppercase tracking-widest text-sm font-bold hover:bg-[var(--accent)] hover:text-white transition-colors">
            Got it
          </button>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const ProductCard = ({ product, index, onSizeGuide, soldOut }) => {
  const { addItem } = useCart();
  const [colorIdx, setColorIdx] = useState(0);
  const [size, setSize] = useState("M");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const color = product.colors[colorIdx];

  const colorOut = (c) => soldOut.includes(`${product.id}::${c}`);
  const sizeOut = (c, s) => colorOut(c) || soldOut.includes(`${product.id}::${c}::${s}`);
  const currentOut = sizeOut(color.name, size);

  const handleAdd = () => {
    if (currentOut) return;
    addItem({
      product_id: product.id, product_name: product.name, price: product.price,
      color: color.name, size, image: color.image,
    });
    toast.success(`Added — ${product.name}`, { description: `${color.name} / ${size} · ${product.price} KSH` });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="border border-[var(--line)] bg-[var(--bg)] group"
      data-testid={`product-card-${product.id}`}
    >
      <button type="button" onClick={() => setGalleryOpen(true)} data-testid={`open-gallery-${product.id}`}
        className="relative aspect-square w-full bg-[var(--surface)] overflow-hidden block cursor-pointer">
        <AnimatePresence mode="wait">
          <motion.img key={color.image} src={color.image} alt={`${product.name} ${color.name}`}
            initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }} className="w-full h-full object-cover" />
        </AnimatePresence>
        <span className="absolute top-4 left-4 bg-black/70 backdrop-blur text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
          {product.type}
        </span>
        <span className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/70 backdrop-blur text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand className="w-3.5 h-3.5" /> Front / Back
        </span>
        {colorOut(color.name) && (
          <span className="absolute top-4 right-4 bg-[var(--text-dim)] text-black text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
            Sold out
          </span>
        )}
      </button>
      <ProductGallery open={galleryOpen} onClose={() => setGalleryOpen(false)} title={`${product.name} — ${color.name}`}
        slides={[{ src: color.image, label: "Back" }, ...(color.front ? [{ src: color.front, label: "Front" }] : [])]} />
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="font-anton uppercase text-2xl md:text-3xl tracking-tight leading-none">{product.name}</h3>
          <span className="font-anton text-2xl text-[var(--accent)] whitespace-nowrap" data-testid={`price-${product.id}`}>{product.price}<span className="text-xs text-[var(--text-dim)] ml-1">KSH</span></span>
        </div>
        <p className="text-[var(--text-dim)] text-sm mb-6 min-h-[40px]">{product.description}</p>

        <div className="flex gap-3 mb-5">
          {product.colors.map((c, i) => {
            const out = colorOut(c.name);
            return (
              <button key={c.name} data-testid={`${product.id}-color-${c.name.toLowerCase()}`}
                onClick={() => !out && setColorIdx(i)} aria-label={c.name} disabled={out}
                className={`relative w-9 h-9 rounded-full border-2 transition-all active:scale-90 ${colorIdx === i ? "border-[var(--accent)] scale-110" : "border-[var(--line)] hover:border-white"} ${out ? "opacity-30 cursor-not-allowed" : ""}`}
                style={{ backgroundColor: c.hex }}>
                {colorIdx === i && !out && <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />}
                {out && <span className="absolute inset-0 flex items-center justify-center text-white text-lg leading-none">×</span>}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            {["S", "M", "L", "XL", "XXL"].map((s) => {
              const out = sizeOut(color.name, s);
              return (
                <button key={s} data-testid={`${product.id}-size-${s}`} onClick={() => !out && setSize(s)} disabled={out}
                  className={`min-w-10 px-3 py-2 border text-xs font-bold uppercase tracking-wider transition-colors active:scale-95 ${size === s && !out ? "bg-[var(--text)] text-black border-[var(--text)]" : "border-[var(--line)] text-[var(--text-dim)] hover:border-white hover:text-white"} ${out ? "opacity-30 line-through cursor-not-allowed hover:border-[var(--line)] hover:text-[var(--text-dim)]" : ""}`}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <button onClick={onSizeGuide} data-testid={`${product.id}-size-guide-btn`}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--text-dim)] hover:text-white transition-colors mb-6">
          <Ruler className="w-4 h-4" /> Size guide
        </button>

        <button data-testid={`${product.id}-add-btn`} onClick={handleAdd} disabled={currentOut}
          className="group/btn w-full flex items-center justify-center gap-3 bg-[var(--accent)] text-white px-6 py-4 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-black transition-colors active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent)] disabled:hover:text-white">
          <Plus className="w-5 h-5 group-hover/btn:rotate-90 transition-transform" /> {currentOut ? "Sold out" : "Add to cart"}
        </button>
      </div>
    </motion.div>
  );
};

const Collection = ({ products, soldOut = [] }) => {
  const [guideOpen, setGuideOpen] = useState(false);
  return (
    <section id="product" className="border-b border-[var(--line)] py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4">
          <h2 className="font-anton uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
            The Drop<span className="text-[var(--accent)]">.</span>
          </h2>
          <p className="text-[var(--text-dim)] uppercase tracking-widest text-sm">
            {products.length} styles · from 1500 KSH
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} soldOut={soldOut} onSizeGuide={() => setGuideOpen(true)} />
          ))}
        </div>
      </div>
      <SizeGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </section>
  );
};

export default Collection;
